import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useQuery, gql } from '@zenbusiness/application-commons-graphql';

import { ADDRESS_FIELDS, CONTACT_FIELDS } from '#client/fragments';
import { generateEntitlements } from '#client/utils/entitlements';

import BusinessProfile from './BusinessProfile';
import NewBusinessProfile from './NewBusinessProfile';

export const GET_PROFILE_QUERY = gql`
  query ProfileQuery($businessEntityUuid: UUID!) {
    businessEntity: businessEntityLegacy(businessEntityUuid: $businessEntityUuid) {
      businessEntityUuid
      businessEntityName
      businessEntityType
      businessIndustry
      physicalAddress {
        ...addressFields
      }
      mailingAddress {
        ...addressFields
      }
      principalAddress {
        ...addressFields
      }
      businessEntityData {
        businessPurpose
        domesticJurisdiction
        ein
        managerType
        directors {
          title
          ...contactFields
        }
        managers {
          ...contactFields
        }
        members {
          ...contactFields
        }
        officers {
          ...contactFields
        }
        authorizedSignatory {
          title
          ...contactFields
        }
        contact {
          ...contactFields
        }
        officialContact: contact {
          ...contactFields
        }
        sopContact {
          ...contactFields
        }
      }
    }
    registeredAgents(businessEntityUuid: $businessEntityUuid) {
      agentInformation {
        ...contactFields
      }
      domesticJurisdiction
      filingDate
      filingID
      isDomestic
      jurisdiction
      electiveCEO {
        ...contactFields
      }
      statePin
    }
    accountInfo {
      accountUuid
      pricing(businessEntityUuid: $businessEntityUuid) {
        service
        amount
      }
    }
    orderTasksLegacy(businessEntityUuid: $businessEntityUuid) {
      orderTasksLegacy {
        orderTaskUuid
        orderTaskStatus
        orderTaskType
      }
    }
    businessEntityAddresses(
      businessEntityUuid: $businessEntityUuid
      addressType: "virtual"
    ) {
      businessEntityUuid
      address {
        ...addressFields
      }
      addressType
    }
  }
  ${ADDRESS_FIELDS}
  ${CONTACT_FIELDS}
`;

const Renderer = () => {
  const { businessEntityUuid } = useBusinessEntity();
  const shouldShowNewBusinessProfile = useExperimentFlag('enable-zendash-business-profile', false);

  const { loading, data } = useQuery(
    GET_PROFILE_QUERY,
    {
      variables: {
        businessEntityUuid
      }
    }
  );
  if (loading) return null;

  const {
    accountInfo: {
      pricing = []
    } = {},
    orderTasksLegacy: {
      orderTasksLegacy = []
    } = {},
    businessEntityAddresses = []
  } = data || {};

  const entitlements = generateEntitlements(orderTasksLegacy);
  // dont show if the virtual address is cancelled
  const virtualAddress = (entitlements.has('virtual_business_address'))
    ? businessEntityAddresses.find((el) => el.addressType === 'virtual')?.address
    : {};

  return (
    <>
      {shouldShowNewBusinessProfile && (
        <NewBusinessProfile
          loading={loading}
          {...data}
          entitlements={entitlements}
          pricing={pricing}
          virtualAddress={virtualAddress}
        />
      )}
      {!shouldShowNewBusinessProfile && (
        <BusinessProfile
          loading={loading}
          {...data}
          entitlements={entitlements}
          pricing={pricing}
          virtualAddress={virtualAddress}
        />
      )}
    </>
  );
};

export default Renderer;
